/**
 * Cacheability rules and key construction. Pure — no I/O, no storage.
 */

export type CacheKind = 'chat' | 'hrms';

/**
 * Endpoints that must never be written to disk. Money and identity data stays
 * live-only so a shared machine never holds it, and short-lived signed URLs
 * would be useless (or misleading) once replayed from cache.
 */
const NEVER_CACHE: RegExp[] = [
  /\/api\/auth\//i,
  /payroll/i,
  /salary/i,
  /tax-declaration/i,
  /face-check/i,
  /\/kiosk/i,
  /\/attachments\/[^/]+\/download/i,
  /signed-url/i,
];

export function isCacheableRequest(
  method: string | undefined,
  url: string | undefined,
): boolean {
  if (!url) return false;
  if ((method ?? 'get').toLowerCase() !== 'get') return false;
  return !NEVER_CACHE.some((re) => re.test(url));
}

export function buildCacheKey(input: {
  ns: string;
  kind: CacheKind;
  url: string;
  params?: Record<string, unknown>;
}): string {
  const { ns, kind, url, params } = input;
  const query = Object.entries(params ?? {})
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => [k, String(v)] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return `${ns}|${kind}|GET ${url}${query ? `?${query}` : ''}`;
}
